import { Checkbox, Icon, ProgressIndicator, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useI18n } from '~/app/i18n/i18n';
import { useTermsAndConditionsDialog } from '~/components/dialog/terms-and-conditions-dialog/terms-and-conditions-dialog';
import { IncentiveLoadingStyles, IncentiveStyle } from '~/components/incentive/styles/incentive-style';
import { SelectionCardStyles } from '~/components/selection-card-new/styles/selection-card-styles';
import { Incentive as IncentiveType } from '~/graphql/__generated__/graphql';
import { DATA_TEST_ID } from '~/test/data-test-id';

interface props {
	selected: boolean;
	select: (componentName?: string) => void;
	alternateDesign?: boolean;
	title?: string | null;
	price?: string | null;
	lowestPriceInfo?: boolean;
	subtitle?: string;
	incentive: IncentiveType;
	isLoading?: boolean;
	// Currently only used when incentive is shown in a popover. When terms and condition dialog is opened, we want the popover to be closed, because this would lead to a stacking problem and the popover would be displayed at the top of the terms and condition dialog. This is a workaround until we have a better solution.
	onOpenTermsAndCondition?: () => void;
}

export const Incentive = ({
	selected,
	title,
	price,
	lowestPriceInfo,
	subtitle,
	select,
	isLoading,
	incentive,
	onOpenTermsAndCondition,
	alternateDesign,
}: props) => {
	const { i18n } = useI18n();
	const { showTermsAndConditionsDialog } = useTermsAndConditionsDialog();

	const labelOrAppendix = alternateDesign ? { appendix: title ?? '', label: '' } : { label: title ?? '' };

	return (
		<SelectionCardStyles alternateDesign={alternateDesign} className={selected ? 'is-selected' : ''}>
			<IncentiveStyle alternateDesign={alternateDesign}>
				<div className="header">
					<div className="selectable">
						<Checkbox
							onClick={() => select()}
							id="incentive"
							value={'incentive'}
							name={'incentive'}
							checked={selected}
							data-testid={DATA_TEST_ID.TACTICAL_CHECKBOX}
							{...labelOrAppendix}
						/>
						{!alternateDesign && (
							<div>
								<Icon name="special-offer-active" data-testid="offer-tag" size="s" />
							</div>
						)}
					</div>
					<div className="price-container">
						{price && (
							<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
								{price}
							</Text>
						)}
						{lowestPriceInfo && (
							<div className="lowest-price-container">
								<Text tag={'p'} variant={{ type: 'copy', order: '3', style: 'normal' }}>
									{i18n('ngc.incentives.lowestPrice')}
								</Text>
							</div>
						)}
					</div>
				</div>
				{}
				<div className="main" tabIndex={0}>
					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
						{subtitle}
					</Text>
				</div>
				<div className="footer">
					<TextLink
						size={'sm'}
						variant={'standalone'}
						onClick={() => {
							onOpenTermsAndCondition && onOpenTermsAndCondition();
							showTermsAndConditionsDialog({
								incentive,
								onSelect: () => select('terms-conditions-layer'),
								showDialogButtons: true,
							});
						}}
						data-testid={DATA_TEST_ID.TACTICAL_MORE_INFORMATION_BUTTON}>
						{i18n('ngc.incentives.termsAndConditions')}
					</TextLink>
				</div>
			</IncentiveStyle>
			{isLoading && (
				<IncentiveLoadingStyles>
					<ProgressIndicator variant={'spinner'} type={'infinite'} />
				</IncentiveLoadingStyles>
			)}
		</SelectionCardStyles>
	);
};
